import get from "lodash.get";
import moment from "moment";
import IntlUtils from "services/intl/utils";

import ParkkiTheme from "themes/ParkkiTheme";

import Abilities from "services/permissions/abilities";
import CommonUtils from "services/common/utils";
import { FormattedMessage } from "react-intl";
import { ArrowUpward, ImportExport } from "@mui/icons-material";
import styled from "@emotion/styled";

class FlowUtils {
  static isRequestAllowed = (scope, type, apiType = undefined) => {
    const config = {
      attendance: ["area", "point"],
      distribution: ["path", "flow"],
      travelTime: ["path", "flow"],
      categoryCount: ["point", "area"],
      general: ["point", "area", "path", "flow", "traffic"],
      details: ["point", "area"],
      chronology: ["point", "area"],
      trafficChart: ["path", "traffic"],
    };

    if (apiType) {
      return config?.[scope]?.indexOf(apiType) >= 0;
    }

    if (config?.[scope]?.indexOf(type) >= 0) {
      return true;
    }

    return false;
  };

  static sortDistributionData = (data, order) => {
    return data.sort((item, other) => {
      const itemIndex = order.indexOf(item?.name);
      const otherIndex = order.indexOf(other.name);

      if (itemIndex < otherIndex) return -1;
      if (itemIndex > otherIndex) return 1;
      return 0;
    });
  };

  static getColorFromType = (type) => {
    if (!type) return "#FFFF";

    switch (type.toLowerCase()) {
      case "car":
        return ParkkiTheme.colors.turquoise2;
      case "two_wheeler":
        return ParkkiTheme.colors.red;
      case "pedestrian":
        return ParkkiTheme.colors.grey;
      case "delivery":
        return ParkkiTheme.colors.orange;
      case "truck":
        return ParkkiTheme.colors.darkOrange;
      case "bus":
        return ParkkiTheme.colors.lightBlue;
      case "motor_bike":
        return ParkkiTheme.colors.flatRed;
      case "bicycle":
        return ParkkiTheme.colors.lightGreen;
      case "all_vehicles":
        return ParkkiTheme.colors.darkBlue;
      default:
        return "#FFFF";
    }
  };

  // @type could dbe "area", "point" or "path"
  static getFilters = (type, state) => {
    let filters = {
      from: state?.Flow?.filters?.from,
      to: state?.Flow?.filters?.to,
      flow_types: state.Flow?.filters?.flowTypes,
    };

    if (type === "path") {
      filters = {
        ...filters,
        maximum_duration_in_minutes:
          state?.Flow?.filters?.maximumDurationInMinutes,
        duration_unit: state?.Flow?.filters?.durationDistributionUnit,
      };
    }

    return filters;
  };

  static getDistributionBody = (id, state) => {
    const distributions = state?.Flow?.filters?.distributions;
    const distributionByPathId = distributions.find(
      (item) => item?.path_id === id
    );

    return { distribution: distributionByPathId?.distribution };
  };

  static getAttendanceFilters = (type = "default", state) => {
    const x_axis = FlowUtils.getAttendanceChartAxis(
      get(state.Flow, "filters", {})
    );
    let from = state?.Flow?.filters?.from;
    let to = state?.Flow?.filters?.to;

    if (x_axis === "5MIN") {
      from =
        state?.Flow?.filters?.from +
        state?.Flow?.filters?.attendance?.range?.fromHour;
      to =
        state?.Flow?.filters?.from +
        state?.Flow?.filters?.attendance?.range?.toHour;
    }

    return {
      ...FlowUtils.getFilters(type, state),
      x_axis,
      from: Math.round(from),
      to: Math.round(to),
    };
  };

  static getTravelTimeFilters = (type = "default", state) => {
    const x_axis = FlowUtils.getTravelTimeChartAxis(state?.Flow?.filters || {});

    return {
      ...FlowUtils.getFilters(type, state),
      x_axis,
    };
  };

  static getAttendanceHourRange = (flowState) => {
    return [
      Math.round(get(flowState, "filters.attendance.range.fromHour") / 3600),
      Math.round(get(flowState, "filters.attendance.range.toHour") / 3600),
    ];
  };

  static getTrafficHourRange = (flowState) => {
    return [
      Math.round(get(flowState, "filters.trafficChart.range.fromHour") / 3600),
      Math.round(get(flowState, "filters.trafficChart.range.toHour") / 3600),
    ];
  };

  static getTravelTimeHourRange = (flowState) => {
    return [
      Math.round(get(flowState, "filters.travelTime.range.fromHour") / 3600),
      Math.round(get(flowState, "filters.travelTime.range.toHour") / 3600),
    ];
  };

  static getDefaultSelectedLayer = (map, floor) => {
    let selectedLayer = FlowUtils.getSelectedLayerFromConfig(
      get(map, "areas", []),
      floor
    );

    if (selectedLayer) {
      return {
        ...selectedLayer,
        type: "area",
      };
    }

    selectedLayer = FlowUtils.getSelectedLayerFromConfig(
      get(map, "points", []),
      floor
    );

    if (selectedLayer) {
      return {
        ...selectedLayer,
        type: "point",
      };
    }

    selectedLayer = FlowUtils.getSelectedLayerFromConfig(
      get(map, "paths", []),
      floor
    );

    if (selectedLayer) {
      return {
        ...selectedLayer,
        type: "path",
      };
    }

    return undefined;
  };

  static getSelectedLayerFromConfig = (layers = [], floor) =>
    layers.find(
      (layer) =>
        layer?.floor_level === floor &&
        layer?.options?.selected_by_default === true
    );

  static getSelectedLayer = (flowState) =>
    get(flowState, "map.selectedLayers", [])?.[0];

  static formatAreasFromApi = (areas = [], config) => {
    const areasToDisplay = areas.filter((area) =>
      config.find(
        (areaConfig) => areaConfig?.area_id && areaConfig?.area_id === area?.id
      )
    );
    return areasToDisplay.map((area) =>
      FlowUtils.formatAreaFromApi(area, config)
    );
  };

  static formatAreaFromApi = (area, config = []) => {
    const areaConfig = config.find(
      (config) =>
        (area?.id || area?.area_id) === (config?.area_id || config?.id)
    );

    return {
      ...areaConfig,
      ...area,
      apiType: area?.type,
      type: "area",
      options: {
        ...(area?.options || {}),
        ...(areaConfig?.display || {}),
      },
    };
  };

  static formatPointsFromApi = (points = [], config) => {
    CommonUtils.resetColorFactoryIndex();

    const pointsToDisplay = points.filter((point) =>
      config.find(
        (pointConfig) =>
          pointConfig?.point_id && pointConfig?.point_id === point?.id
      )
    );
    return pointsToDisplay.map((point) =>
      FlowUtils.formatPointFromApi(point, config)
    );
  };

  static formatPointFromApi = (point, config = []) => {
    const pointConfig = config.find(
      (config) =>
        (point?.id || point?.point_id) === (config?.point_id || config?.id)
    );

    return {
      ...pointConfig,
      ...point,
      apiType: point?.type,
      type: "point",
      location: pointConfig?.display?.location || point?.location,
      options: {
        ...(point?.options || {}),
        ...(pointConfig?.display || {}),
        selection_color:
          pointConfig?.display?.selection_color ||
          pointConfig?.options?.selection_color ||
          CommonUtils.getNewColor(),
      },
    };
  };

  static formatPathsFromApi = (paths = [], config) => {
    CommonUtils.resetColorFactoryIndex();

    const pathsToDisplay = paths.filter((path) =>
      config.find(
        (pathConfig) => pathConfig?.path_id && pathConfig?.path_id === path?.id
      )
    );
    return pathsToDisplay.map((path) =>
      FlowUtils.formatPathFromApi(path, config)
    );
  };

  static formatPathFromApi = (path, config = []) => {
    const pathConfig = config.find(
      (pathConfig) =>
        (path?.id || path?.path_id) === (pathConfig?.path_id || pathConfig?.id)
    );

    return {
      ...pathConfig,
      ...path,
      positions: pathConfig?.display?.polylines || path?.polylines,
      apiType: path?.type,
      type: "path",
      options: {
        ...(path?.options || {}),
        ...(pathConfig?.display || {}),
        selection_color:
          pathConfig?.display?.selection_color ||
          pathConfig?.options?.selection_color ||
          CommonUtils.getNewColor(),
      },
    };
  };

  static formatLayersInfosFromApi = (layersInfos, config) => {
    return {
      ...layersInfos,
      areas: FlowUtils.formatAreasFromApi(
        layersInfos?.areas || [],
        config?.areas || []
      ),
      points: FlowUtils.formatPointsFromApi(
        layersInfos?.points || [],
        config?.points || []
      ),
      paths: FlowUtils.formatPathsFromApi(
        layersInfos?.paths || [],
        config?.paths || []
      ),
    };
  };

  static formatChronologyGraphFromApi = (graph, config) => {
    return {
      ...graph,
      stats: get(graph, "stats", []).map((stat) => {
        const pointConfig = get(config, "points", []).find(
          (config) =>
            get(stat, "id", get(stat, "point_id")) ===
            get(config, "point_id", get(config, "id"))
        );

        return {
          ...stat,
          short_name: get(pointConfig, "short_name", ""),
          long_name: get(pointConfig, "long_name", ""),
          display: {
            ...get(stat, "display", {}),
            ...get(pointConfig, "options", {}),
          },
        };
      }),
    };
  };

  static formatCategoryCountGraphFromApi = (graph, config) => {
    const statsByType = {};
    for (const stat of graph.stats) {
      const typeName = stat.type;
      if (!statsByType[typeName]) {
        statsByType[typeName] = [];
      }
      statsByType[typeName].push(stat);
    }

    const aggregated = {};

    for (const [typeName, stats] of Object.entries(statsByType)) {
      aggregated[typeName] = {};

      const firstStat = stats[0];
      const timestamps = Object.keys(firstStat.data);

      for (const timestamp of timestamps) {
        const values = stats.map((stat) => stat.data[timestamp]);

        if (values.every((v) => v === null)) {
          aggregated[typeName][timestamp] = null;
        } else {
          aggregated[typeName][timestamp] = values.reduce(
            (sum, val) => sum + (val === null ? 0 : val),
            0
          );
        }
      }
    }

    const result = {
      type: graph.type,
      from: graph.from,
      to: graph.to,
      stats: [],
    };

    for (const [typeName, timeData] of Object.entries(aggregated)) {
      result.stats.push({
        type: typeName,
        data: timeData,
      });
    }

    return result;
  };

  static getTotalMouvements = (stats) =>
    stats.reduce(
      (acc, currentValue) =>
        acc + get(currentValue, "totalMouvements.value", 0),
      0
    );

  static formatDetailsStatsFromApi = (stats, flowState) => {
    const type = stats?.type || "point";

    if (type === "point") {
      return FlowUtils.formatPointDetailsStatsFromApi(stats, flowState);
    }
    if (type === "area") {
      return FlowUtils.formatAreaDetailsStatsFromApi(stats, flowState);
    }

    return undefined;
  };

  static formatAreaDetailsStatsFromApi = (stats, flowState) => {
    CommonUtils.resetColorFactoryIndex();
    let areaAttendanceBar;
    let itemsDetails = (flowState?.flowDetails?.items || [])?.reduce(
      (result, currentItem) => {
        if (!currentItem?.point_id) {
          return result;
        }

        return {
          ...result,
          [`${currentItem?.point_id}-${currentItem?.reqFilters?.from}-${currentItem?.reqFilters?.to}`]:
            currentItem,
        };
      },
      {}
    );

    // First, we format the stats and calculate the totalMouvements for each stats
    let formattedStats = {
      ...stats,
      in: get(stats, "in", []).map((point) => {
        const detailsStatsForAPoint =
          FlowUtils.computeDetailsStatsForAPoint(point);
        const hash = `${point.point_id}-${stats?.reqFilters?.from}-${stats?.reqFilters?.to}`;

        const valueIn = itemsDetails[hash]?.background?.in || 0;
        const valueOut = detailsStatsForAPoint?.totalMouvements?.value;

        itemsDetails = {
          ...itemsDetails,
          [hash]: {
            ...(itemsDetails[hash] || {}),
            reqFilters: stats?.reqFilters,
            point_id: point.point_id,
            background: {
              out: 0,
              ...(itemsDetails[hash]?.background || {}),
              config: ParkkiTheme.flowAttendanceBar,
              in: detailsStatsForAPoint?.totalMouvements?.value,
              value: Math.round((valueIn / (valueIn + valueOut)) * 100),
              total: 100,
            },
          },
        };

        return {
          ...FlowUtils.formatPointFromApi(point, flowState?.map?.points),
          ...detailsStatsForAPoint,
        };
      }),
      out: get(stats, "out", []).map((point) => {
        const detailsStatsForAPoint =
          FlowUtils.computeDetailsStatsForAPoint(point);
        const hash = `${point.point_id}-${stats?.reqFilters?.from}-${stats?.reqFilters?.to}`;

        const valueIn = itemsDetails[hash]?.background?.in || 0;
        const valueOut = detailsStatsForAPoint?.totalMouvements?.value;

        itemsDetails = {
          ...itemsDetails,
          [hash]: {
            ...(itemsDetails[hash] || {}),
            reqFilters: stats?.reqFilters,
            point_id: point.point_id,
            background: {
              in: 0,
              ...(itemsDetails[hash]?.background || {}),
              config: ParkkiTheme.flowAttendanceBar,
              out: valueOut,
              value: Math.round((valueIn / (valueIn + valueOut)) * 100),
              total: 100,
            },
          },
        };

        return {
          ...FlowUtils.formatPointFromApi(point, flowState?.map?.points),
          ...detailsStatsForAPoint,
        };
      }),
    };

    // Then, we compute the totalMouvements percentage
    const totalMouvementsIn = FlowUtils.getTotalMouvements(
      get(formattedStats, "in", [])
    );
    const totalMouvementsOut = FlowUtils.getTotalMouvements(
      get(formattedStats, "out", [])
    );

    if (
      ["FLOW", "PARKANDFLOW", "PEDESTRIANANDFLOW"].indexOf(stats?.apiType) >= 0
    ) {
      areaAttendanceBar = {
        config: ParkkiTheme.flowAttendanceBar,
        in: totalMouvementsIn,
        out: totalMouvementsOut,
        value: Math.round(
          (totalMouvementsIn / (totalMouvementsIn + totalMouvementsOut)) * 100
        ),
        total: 100,
      };
    }

    formattedStats = {
      ...formattedStats,
      in: get(formattedStats, "in", [])
        .map((point) =>
          FlowUtils.computeTotalMouvementsPercentageForAPoint(
            point,
            totalMouvementsIn
          )
        )
        .sort((a, b) => {
          const shortNameA = get(a, "short_name", "").toUpperCase();
          const shortNameB = get(b, "short_name", "").toUpperCase();
          return shortNameA < shortNameB ? -1 : shortNameA > shortNameB ? 1 : 0;
        }),
      out: get(formattedStats, "out", [])
        .map((point) =>
          FlowUtils.computeTotalMouvementsPercentageForAPoint(
            point,
            totalMouvementsOut
          )
        )
        .sort((a, b) => {
          const shortNameA = get(a, "short_name", "").toUpperCase();
          const shortNameB = get(b, "short_name", "").toUpperCase();
          return shortNameA < shortNameB ? -1 : shortNameA > shortNameB ? 1 : 0;
        }),
      items: [
        ...(Object.values(itemsDetails) || []),
        {
          reqFilters: stats?.reqFilters,
          area_id: formattedStats?.area_id,
          background: areaAttendanceBar,
        },
      ],
      availableFlowTypes: flowState?.filters?.availableFlowTypes,
    };

    return formattedStats;
  };

  static percentDistribution = (array) => {
    const arrayCopy = array.map((obj) => {
      return {
        ...obj,
        percentage: Math.trunc(obj.percentage * 100 * 10) / 10,
      };
    });

    const percentagesTotal = arrayCopy.reduce(
      (acc, obj) => (acc += obj.percentage),
      0
    );

    const diff = Math.round((100 - percentagesTotal) * 10) / 10;

    const indexOfBiggestNumber = arrayCopy
      .map((obj) => obj.percentage)
      .indexOf(Math.max(...arrayCopy.map((obj) => obj.percentage)));

    if (indexOfBiggestNumber !== -1) {
      arrayCopy[indexOfBiggestNumber].percentage =
        Math.round((arrayCopy[indexOfBiggestNumber].percentage + diff) * 10) /
        10;
    }
    return arrayCopy;
  };

  static formatPointDetailsStatsFromApi = (stats, flowState) => {
    const totalIn = get(stats, "in", []).reduce(
      (total, point) => total + get(point, "value", 0),
      0
    );
    const totalOut = get(stats, "out", []).reduce(
      (total, point) => total + get(point, "value", 0),
      0
    );

    const formatDirectionsPercentages = (formattedStats) => {
      const { in: inArr, out: outArr } = formattedStats;

      return {
        in: this.percentDistribution(inArr),
        out: this.percentDistribution(outArr),
      };
    };

    let itemsDetails = (flowState?.flowDetails?.items || [])?.reduce(
      (result, currentItem) => {
        if (!currentItem?.point_id) {
          return result;
        }

        return {
          ...result,
          [`${currentItem?.point_id}-${currentItem?.reqFilters?.from}-${currentItem?.reqFilters?.to}`]:
            currentItem,
        };
      },
      {}
    );

    const hash = `${stats.point_id}-${stats?.reqFilters?.from}-${stats?.reqFilters?.to}`;

    const formattedStats = {
      ...stats,
      in: get(stats, "in", []).map((point) => {
        itemsDetails = {
          ...itemsDetails,
          [hash]: {
            ...(itemsDetails[hash] || {}),
            reqFilters: stats?.reqFilters,
            point_id: stats.point_id,
            background: {
              out: 0,
              ...(itemsDetails[hash]?.background || {}),
              config: ParkkiTheme.flowAttendanceBar,
              in: totalIn,
              value: Math.round((totalIn / (totalIn + totalOut)) * 100),
              total: 100,
            },
          },
        };

        return {
          ...point,
          percentage: totalIn ? get(point, "value", 0) / totalIn || 0 : 0,
        };
      }),
      out: get(stats, "out", []).map((point) => {
        itemsDetails = {
          ...itemsDetails,
          [hash]: {
            ...(itemsDetails[hash] || {}),
            reqFilters: stats?.reqFilters,
            point_id: stats.point_id,
            background: {
              in: 0,
              ...(itemsDetails[hash]?.background || {}),
              config: ParkkiTheme.flowAttendanceBar,
              out: totalOut,
              value: Math.round((totalIn / (totalIn + totalOut)) * 100),
              total: 100,
            },
          },
        };

        return {
          ...point,
          percentage: totalOut ? get(point, "value", 0) / totalOut || 0 : 0,
        };
      }),
      percentageIn: totalIn / (totalIn + totalOut),
      percentageOut: totalOut / (totalIn + totalOut),
    };

    const formattedPercentages = formatDirectionsPercentages(formattedStats);

    return {
      ...formattedStats,
      items: Object.values(itemsDetails),
      in: formattedPercentages.in,
      out: formattedPercentages.out,
    };
  };

  static computeTotalMouvementsPercentageForAPoint = (
    point,
    totalMouvements
  ) => ({
    ...point,
    totalMouvements: {
      ...get(point, "totalMouvements"),
      percentage: (point?.totalMouvements?.value || 0) / totalMouvements || 0,
    },
  });

  static computeDetailsStatsForAPoint = (point) => {
    const totalMouvements = get(point, "stats").reduce(
      (acc, mouvement) => acc + get(mouvement, "value", 0),
      0
    );

    return {
      ...point,
      totalMouvements: {
        value: totalMouvements,
      },
      stats: this.percentDistribution(
        point.stats.map((mouvement) => ({
          ...mouvement,
          percentage: (mouvement.value ?? 0) / totalMouvements || 0,
        })) ?? []
      ),
    };
  };

  static getWay = (value, avg) => {
    const diff = value - avg;

    if (diff > 0) {
      return 1;
    }
    if (diff < 0) {
      return -1;
    }

    return 0;
  };

  static getColor = (value, avg) => {
    const way = FlowUtils.getWay(value, avg);
    return way >= 0 ? "green" : "red";
  };

  static getPathGeneralStatsRow = (stats) => {
    const intl = IntlUtils.getIntlInstance();

    const apiType = stats?.apiType;

    if (apiType === "TRAFFIC") {
      return [
        {
          toggle: true,
          headerColor: ParkkiTheme.colors.lightGreen,
          label: intl.messages["flowScene.flowSection.statsRow.traffic.fluid"],
          value: {
            value: stats?.stats?.FLUID,
            formatValue: (val) => {
              if (val === 0) {
                return "-";
              }
              return CommonUtils.humanizeDateFromDuration(
                val,
                "seconds",
                intl,
                false
              );
            },
          },
          type: {
            toggle: false,
            name: "normal",
          },
        },
        {
          toggle: true,
          headerColor: ParkkiTheme.colors.darkOrange,
          label: intl.messages["flowScene.flowSection.statsRow.traffic.loaded"],
          value: {
            value: stats?.stats?.LOADED,
            formatValue: (val) => {
              if (val === 0) {
                return "-";
              }
              return CommonUtils.humanizeDateFromDuration(
                val,
                "seconds",
                intl,
                false
              );
            },
          },
          type: {
            toggle: false,
            name: "normal",
          },
        },
        {
          toggle: true,
          headerColor: ParkkiTheme.colors.flatRed,
          label:
            intl.messages["flowScene.flowSection.statsRow.traffic.saturated"],
          value: {
            value: stats?.stats?.SATURATED,
            formatValue: (val) => {
              if (val === 0) {
                return "-";
              }
              return CommonUtils.humanizeDateFromDuration(
                val,
                "seconds",
                intl,
                false
              );
            },
          },
          type: {
            toggle: false,
            name: "normal",
          },
        },
        // To be confirmed if the client wants to display it
        {
          toggle: false,
          headerColor: ParkkiTheme.colors.darkGrey,
          label: intl.messages["flowScene.flowSection.statsRow.traffic.fault"],
          value: {
            value: stats?.stats?.FAULT,
            formatValue: (val) => {
              if (val === 0) {
                return "-";
              }
              return CommonUtils.humanizeDateFromDuration(
                val,
                "seconds",
                intl,
                false
              );
            },
          },
          type: {
            toggle: false,
            name: "normal",
          },
        },
      ];
    }

    return [
      {
        toggle: Abilities.ability.can(
          "read",
          "flow.paths.keys.number_of_passages"
        ),
        headerColor: ParkkiTheme.colors.turquoise,
        label: intl.messages["flowScene.flowSection.statsRow.numberOfPassages"],
        value: {
          value: stats?.number_of_passages,
          formatValue: (val) => val,
        },
        type: {
          toggle: false,
          name: "normal",
        },
      },
      {
        toggle: Abilities.ability.can(
          "read",
          "flow.paths.keys.travel_time_median"
        ),
        headerColor: ParkkiTheme.colors.orange,
        label: intl.messages["flowScene.flowSection.statsRow.travelTimeMedian"],
        value: {
          value: stats?.travel_time_median,
          formatValue: (val) => {
            if (stats.duration_unit === "MINUTES") {
              if (val < 1) {
                return (
                  <span>
                    {
                      intl.messages[
                        "flowScene.flowSection.statsRow.lessThanOneMinute"
                      ]
                    }
                  </span>
                );
              }
              return CommonUtils.humanizeMinutesToHours(val);
            } else if (stats.duration_unit === "SECONDS") {
              return CommonUtils.humanizeSecondsToMinutes(val);
            }
          },
        },
        type: {
          toggle: false,
          name: "normal",
        },
      },
      {
        toggle: Abilities.ability.can(
          "read",
          "flow.paths.keys.travel_time_avg"
        ),
        headerColor: ParkkiTheme.colors.red,
        label: intl.messages["flowScene.flowSection.statsRow.travelTimeAvg"],
        value: {
          value: stats?.travel_time_avg,
          formatValue: (val) => {
            if (stats.duration_unit === "MINUTES") {
              if (val < 1) {
                return (
                  <span>
                    {
                      intl.messages[
                        "flowScene.flowSection.statsRow.lessThanOneMinute"
                      ]
                    }
                  </span>
                );
              }
              return CommonUtils.humanizeMinutesToHours(val);
            } else if (stats.duration_unit === "SECONDS") {
              return CommonUtils.humanizeSecondsToMinutes(val);
            }
          },
        },
        type: {
          toggle: false,
          name: "normal",
        },
      },
      {
        toggle:
          Abilities.ability.can("read", "flow.paths.keys.travel_speed_avg") &&
          stats?.travel_speed_avg_in_kmh > 0.5,
        headerColor: ParkkiTheme.colors.lightGreen,
        label: intl.messages["flowScene.flowSection.statsRow.speedAvg"],
        value: {
          value: stats?.travel_speed_avg_in_kmh,
          formatValue: (val) => (
            <>
              {Math.round(val)}
              <span style={{ fontSize: "14px", marginLeft: "3px" }}>km/h</span>
            </>
          ),
        },
        type: {
          toggle: false,
          name: "normal",
        },
      },
    ];
  };

  static getGeneralStatsRow = (stats, traffic) => {
    const intl = IntlUtils.getIntlInstance();
    if (stats?.type === "path") {
      return this.getPathGeneralStatsRow(stats);
    }

    return [
      {
        toggle: Abilities.ability.can("read", "flow.keys.in"),
        headerColor: ParkkiTheme.colors.blue,
        label: traffic?.labels?.in ?? (
          <FormattedMessage
            id="flowScene.flowSection.statsRow.generalStats.in"
            values={{ count: stats?.flow?.in?.value ?? "-" }}
          />
        ),
        infos: intl.messages["flowScene.flowSection.statsRow.infos.in"],
        value: {
          value: stats?.flow?.in?.value ?? "-",
          formatValue: (val) => val,
          formatNormal: (val) => val,
        },
        type: {
          toggle: Abilities.ability.can("read", "flow.keys.in"),
          name: "normal",
          retrieveNormal: () => get(stats, "flow.in.avg", "-"),
          // way: () => FlowUtils.getWay(get(stats, "flow.in.value"), get(stats, "flow.in.avg")),
          retrieveColor: () =>
            FlowUtils.getColor(
              get(stats, "flow.in.value"),
              get(stats, "flow.in.avg")
            ),
          symbol: "sign",
          unity: "",
          valueUnity: "",
        },
      },
      {
        toggle: Abilities.ability.can("read", "flow.keys.out"),
        headerColor: ParkkiTheme.colors.turquoise,
        label: traffic?.labels?.out ?? (
          <FormattedMessage
            id="flowScene.flowSection.statsRow.generalStats.out"
            values={{ count: stats?.flow?.out?.value ?? "-" }}
          />
        ),
        infos: intl.messages["flowScene.flowSection.statsRow.infos.out"],
        value: {
          value: stats?.flow?.out?.value ?? "-",
          formatValue: (val) => val,
          formatNormal: (val) => val,
        },
        type: {
          toggle: Abilities.ability.can("read", "flow.keys.out"),
          name: "normal",
          retrieveNormal: () => get(stats, "flow.out.avg", "-"),
          // way: () => FlowUtils.getWay(get(stats, "flow.out.value"), get(stats, "flow.out.avg")),
          retrieveColor: () =>
            FlowUtils.getColor(
              get(stats, "flow.out.value"),
              get(stats, "flow.out.avg")
            ),
          symbol: "sign",
          unity: "",
          valueUnity: "",
        },
      },
      {
        toggle: Abilities.ability.can("read", "flow.keys.in.max"),
        headerColor: ParkkiTheme.colors.red,
        label:
          get(stats, "peak.in.date") !== undefined &&
          get(stats, "peak.in.date") !== null
            ? intl.formatMessage(
                { id: "flowScene.flowSection.statsRow.maxStats.the" },
                {
                  day: moment(get(stats, "peak.in.date", 0) * 1000).format(
                    "D MMMM YYYY"
                  ),
                }
              )
            : "",
        subLabel:
          get(stats, "peak.in.date") !== undefined &&
          get(stats, "peak.in.date") !== null
            ? intl.formatMessage(
                { id: "flowScene.flowSection.statsRow.maxStats.at" },
                {
                  hourStart: moment(
                    get(stats, "peak.in.date", 0) * 1000
                  ).format("HH:mm"),
                  hourEnd: moment(get(stats, "peak.in.date", 0) * 1000)
                    .add(1, "hour")
                    .format("HH:mm"),
                }
              )
            : "",
        // infos: intl.messages["flowScene.flowSection.statsRow.infos.in"],
        value: {
          label: traffic?.labels?.in ?? (
            <FormattedMessage
              id="flowScene.flowSection.statsRow.maxStats.in"
              values={{ count: stats?.peak?.in?.value ?? "-" }}
            />
          ),
          subLabel:
            intl.messages["flowScene.flowSection.statsRow.maxStats.max"],
          value: stats?.peak?.in?.value ?? "-",
          formatValue: (val) => val,
          formatNormal: (val) => val,
        },
        type: {
          toggle: Abilities.ability.can("read", "flow.keys.in.max"),
          name: "max",
        },
      },
      {
        toggle: Abilities.ability.can("read", "flow.keys.out.max"),
        headerColor: ParkkiTheme.colors.orange,
        label:
          get(stats, "peak.out.date") !== undefined &&
          get(stats, "peak.out.date") !== null
            ? intl.formatMessage(
                { id: "flowScene.flowSection.statsRow.maxStats.the" },
                {
                  day: moment(get(stats, "peak.out.date", 0) * 1000).format(
                    "D MMMM YYYY"
                  ),
                }
              )
            : "",
        subLabel:
          get(stats, "peak.out.date") !== undefined &&
          get(stats, "peak.out.date") !== null
            ? intl.formatMessage(
                { id: "flowScene.flowSection.statsRow.maxStats.at" },
                {
                  hourStart: moment(
                    get(stats, "peak.out.date", 0) * 1000
                  ).format("HH:mm"),
                  hourEnd: moment(get(stats, "peak.out.date", 0) * 1000)
                    .add(1, "hour")
                    .format("HH:mm"),
                }
              )
            : "",
        // infos: intl.messages["flowScene.flowSection.statsRow.infos.out"],
        value: {
          label: traffic?.labels?.out ?? (
            <FormattedMessage
              id="flowScene.flowSection.statsRow.maxStats.out"
              values={{ count: stats?.peak?.out?.value ?? "-" }}
            />
          ),
          subLabel:
            intl.messages["flowScene.flowSection.statsRow.maxStats.max"],
          value: stats?.peak?.out?.value,
          formatValue: (val) => val,
          // ...GlobalFiltersUtils.getStatsByKey("general", "avg_parking_duration"),
          // formatValue: val => CommonUtils.humanizeMinutesToHours(val),
        },
        type: {
          toggle: Abilities.ability.can("read", "flow.keys.out.max"),
          order: "last",
          name: "max",
        },
      },
    ];
  };

  static getAttendanceChartAxis = (filters) => {
    let axis = get(filters, "attendance.x_axis");
    const from = get(filters, "from");
    const to = get(filters, "to");
    const isMultipleDays =
      moment(to * 1000).diff(moment(from * 1000), "days") > 0;
    const groupByHours = get(filters, "attendance.group_by_hours", false);
    if (!axis) {
      if (isMultipleDays) {
        axis = groupByHours ? "HOUR" : "DAY";
      } else {
        axis = "HOUR";
      }
    }

    return axis;
  };

  static getAttendanceChartGroupByHour = (filters) => {
    return get(filters, "attendance.groupByHour", false);
  };

  static getAttendanceLimitTime = (stats) => {
    const attendanceConfig = stats?.attendance ?? {};
    return (
      attendanceConfig?.timeLimit ?? {
        left_time_limit: 0,
        right_time_limit: 24,
      }
    );
  };

  static getTrafficLimitTime = (stats) => {
    return (
      stats?.trafficChart?.timeLimit ?? {
        left_time_limit: 0,
        right_time_limit: 24,
      }
    );
  };

  static getTravelTimeLimit = (stats) => {
    return (
      stats?.travelTime?.timeLimit ?? {
        left_time_limit: 0,
        right_time_limit: 24,
      }
    );
  };

  static getTravelTimeChartAxis = (filters) => {
    const axis = filters?.travelTime?.x_axis || "HOUR";
    return axis;
  };

  static getTrafficChart = (stats, intl) => {
    const axisType = stats?.filters?.trafficChart?.x_axis || "HOUR";
    const range = stats?.filters?.trafficChart?.range || {
      fromHour: 9 * 3600,
      toHour: 18 * 3600,
    };

    const fromHour = range.fromHour / 3600;
    const toHour = range.toHour / 3600;

    const filteredStats =
      axisType !== "5MIN"
        ? stats?.trafficChart?.stats
        : stats?.trafficChart?.stats?.filter((v) => {
            const timeUnit = v.time_unit;
            const timeUnitHour = moment(timeUnit).hour();
            return timeUnitHour >= fromHour && timeUnitHour <= toHour;
          });

    const labels =
      filteredStats?.map((v) => {
        switch (axisType) {
          case "DAY":
            return moment(v.time_unit).format("DD/MM/YYYY");
          case "HOUR":
            return v.time_unit.includes(" ")
              ? moment(v.time_unit).format("DD/MM HH:00")
              : `${v.time_unit}h`;
          case "5MIN":
            return moment(v.time_unit).format("DD/MM HH:mm");
          default:
            return v;
        }
      }) ?? [];

    // Create datasets with the new structure: [{backgroundColor: "...", label: 'Fluide', data : [...]},...]
    const datasets = [
      {
        backgroundColor: ParkkiTheme.colors.darkGrey,
        label: intl.messages["flowScene.traffic.fault"] || "Défaut",
        data: filteredStats?.map((stat) => stat.FAULT) || [],
      },
      {
        backgroundColor: ParkkiTheme.colors.flatRed,
        label: intl.messages["flowScene.traffic.saturated"] || "Saturé",
        data: filteredStats?.map((stat) => stat.SATURATED) || [],
      },
      {
        backgroundColor: ParkkiTheme.colors.darkOrange,
        label: intl.messages["flowScene.traffic.loaded"] || "Chargé",
        data: filteredStats?.map((stat) => stat.LOADED) || [],
      },
      {
        backgroundColor: ParkkiTheme.colors.lightGreen,
        label: intl.messages["flowScene.traffic.fluid"] || "Fluide",
        data: filteredStats?.map((stat) => stat.FLUID) || [],
      },
    ];

    return {
      toggle: true,
      labels,
      datasets,
    };
  };

  static getTrafficChartOptions = (stats, intl) => {
    const axisType = stats?.filters?.traffic?.x_axis || "HOUR";
    const durationUnit = stats?.filters?.durationDistributionUnit;
    return {
      maintainAspectRatio: false,
      responsive: true,
      tooltips: {
        enabled: true,
        mode: "index",
        callbacks: {
          title: (tooltipItems, data) =>
            `${
              intl.messages[
                `flowScene.charts.attendance.${
                  axisType !== "DAY"
                    ? "tooltipTitlePrefixAt"
                    : "tooltipTitlePrefixThe"
                }`
              ]
            } ${get(data, "labels", {})[tooltipItems[0].index]}`,
          label: (
            tooltipItem = { datasetIndex: null, index: null },
            data = { datasets: [] }
          ) => {
            const { datasetIndex, index } = tooltipItem;
            const { datasets } = data;
            const currentDataset =
              datasetIndex >= 0 &&
              datasets[datasetIndex] !== null &&
              datasets[datasetIndex] !== undefined
                ? datasets[datasetIndex]
                : null;

            return `${currentDataset.label}: ${
              CommonUtils.humanizeDateFromDuration(
                currentDataset?.data?.[index],
                durationUnit.toLowerCase(),
                intl,
                false
              ) || "0s"
            }`;
          },
        },
      },
      scales: {
        xAxes: [
          {
            stacked: true,
            categoryPercentage: 1.0,
            barPercentage: 0.95,
            ticks: {
              autoSkip: true,
              maxTicksLimit: 10,
            },
            gridLines: {
              display: false,
            },
          },
        ],
        yAxes: [
          {
            stacked: true,
            ticks: {
              beginAtZero: true,
              userCallback: (value) => {
                if (durationUnit === "SECONDS") {
                  return CommonUtils.humanizeDateFromDuration(
                    value,
                    durationUnit.toLowerCase(),
                    intl,
                    false
                  );
                }
                const hours = Math.floor(value / 60);
                const minutes = Math.floor(value % 60);
                const seconds = Math.round((value - Math.floor(value)) * 60);

                return (
                  (hours > 0 ? `${hours}h` : "") +
                  (minutes > 0 ? `${minutes}m` : "") +
                  (seconds > 0 ? `${seconds}s` : "")
                );
              },
            },
          },
        ],
      },
    };
  };

  static getCategoryCountChart = (stats, intl) => {
    const axisType = stats?.filters?.categoryCount?.x_axis || "HOUR";

    const labels = Object.keys(
      stats?.categoryCount?.stats?.[0]?.data ?? {}
    ).map((v) => {
      switch (axisType) {
        case "DAY":
          return moment(v).format("DD/MM/YYYY");
        case "HOUR":
          return v.includes(" ") ? moment(v).format("DD/MM HH:00") : `${v}h`;
        default:
          return v;
      }
    });

    const datasets =
      stats?.categoryCount?.stats?.map((stat) => ({
        label:
          intl.messages[
            `flowScene.filters.flowTypes.${stat.type.toLowerCase()}`
          ],
        data: Object.values(stat.data),
        backgroundColor: FlowUtils.getColorFromType(stat.type.toLowerCase()),
        apiType: stat.type,
      })) ?? [];

    return {
      toggle: true,
      labels,
      datasets,
    };
  };

  static getCategoryCountChartOptions = (stats, intl) => {
    const axisType = stats?.filters?.categoryCount?.x_axis || "HOUR";
    return {
      maintainAspectRatio: false,
      responsive: true,
      tooltips: {
        enabled: true,
        mode: "index",
        callbacks: {
          title: (tooltipItems, data) =>
            `${
              intl.messages[
                `flowScene.charts.attendance.${
                  axisType !== "DAY"
                    ? "tooltipTitlePrefixAt"
                    : "tooltipTitlePrefixThe"
                }`
              ]
            } ${get(data, "labels", {})[tooltipItems[0].index]}`,
          label: (tooltipItem, data) => {
            const { datasetIndex, index } = tooltipItem;
            const { datasets } = data;
            const currentDataset =
              datasetIndex >= 0 &&
              datasets[datasetIndex] !== null &&
              datasets[datasetIndex] !== undefined
                ? datasets[datasetIndex]
                : null;
            return `${currentDataset?.data?.[index]} ${intl.formatMessage(
              {
                id: `flowScene.flowType.${currentDataset?.apiType.toLowerCase()}`,
              },
              { count: currentDataset?.data?.[index] }
            )}`;
          },
        },
      },
      scales: {
        xAxes: [
          {
            stacked: true,
            categoryPercentage: 1.0,
            barPercentage: 0.95,
            ticks: {
              autoSkip: true,
              maxTicksLimit: 10,
            },
            gridLines: {
              display: false,
            },
          },
        ],
        yAxes: [
          {
            stacked: true,
          },
        ],
      },
    };
  };

  static getAttendanceChart = (stats, intl) => {
    const axisType = FlowUtils.getAttendanceChartAxis(
      get(stats, "filters", {})
    );

    const attendanceRealTime = stats?.attendance?.real_time ?? {};
    const attendancePrevisionalTime = stats?.attendance?.previsional ?? {};

    const labels = Object.keys(attendanceRealTime).map((v) => {
      switch (axisType) {
        case "DAY":
          return moment(v).format("DD/MM/YYYY");
        case "HOUR":
          return v.includes(" ") ? moment(v).format("DD/MM HH:00") : `${v}h`;
        case "5MIN":
          return v;
        default:
          return v;
      }
    });

    return {
      toggle: true,
      labels,
      datasets: [
        {
          type: "line",
          label: intl.messages["flowScene.charts.attendance.realTime"],
          data: Object.values(attendanceRealTime),
          fill: false,
          borderColor: ParkkiTheme.colors.red,
          backgroundColor: ParkkiTheme.colors.red,
          pointBorderColor: ParkkiTheme.colors.red,
          pointBackgroundColor: ParkkiTheme.colors.red,
          pointHoverBackgroundColor: ParkkiTheme.colors.red,
          pointHoverBorderColor: ParkkiTheme.colors.red,
        },
        {
          type: "bar",
          label: intl.messages["flowScene.charts.attendance.previsional"],
          data: Object.values(attendancePrevisionalTime),
          backgroundColor:
            axisType !== "DAY"
              ? FlowUtils.getHourBackgroundColorArray(
                  get(
                    Object.values(attendanceRealTime).filter(
                      (item) => item !== null
                    ),
                    "length",
                    0
                  ) - 1,
                  Object.values(attendancePrevisionalTime).length,
                  axisType
                )
              : FlowUtils.getDayBackgroundColorArray(
                  Object.values(attendanceRealTime),
                  axisType
                ),
          borderColor: ParkkiTheme.colors.turquoise,
          hoverBackgroundColor: ParkkiTheme.colors.turquoise,
          hoverBorderColor: ParkkiTheme.colors.turquoise,
        },
      ],
    };
  };

  static getTravelTimeChart = (stats, intl) => {
    const axisType = FlowUtils.getTravelTimeChartAxis(stats?.filters);
    const fromHour = stats?.filters?.travelTime?.range?.fromHour / 3600;
    const toHour = stats?.filters?.travelTime?.range?.toHour / 3600;
    const timeLimit = FlowUtils.getTravelTimeLimit(stats);
    let realTimeData = CommonUtils.formatGraphWithTimeLimit(
      stats?.travelTime?.real_time || {},
      timeLimit
    );
    let previsionalData = CommonUtils.formatGraphWithTimeLimit(
      stats?.travelTime?.previsional || {},
      timeLimit
    );

    if (axisType === "5MIN") {
      realTimeData = Object.keys(realTimeData).reduce(
        (currentResult, currentKey) => {
          const currentKeyHours = parseInt(currentKey?.split(":")[0], 10);
          const currentKeyMinutes = parseInt(currentKey?.split(":")[1], 10);

          if (
            (currentKeyHours >= fromHour && currentKeyHours < toHour) ||
            (currentKeyHours === toHour && currentKeyMinutes === 0)
          ) {
            return {
              ...currentResult,
              [currentKey]: stats?.travelTime?.real_time?.[currentKey],
            };
          }

          return currentResult;
        },
        {}
      );
      previsionalData = Object.keys(previsionalData).reduce(
        (currentResult, currentKey) => {
          const currentKeyHours = parseInt(currentKey?.split(":")[0], 10);
          const currentKeyMinutes = parseInt(currentKey?.split(":")[1], 10);

          if (
            (currentKeyHours >= fromHour && currentKeyHours < toHour) ||
            (currentKeyHours === toHour && currentKeyMinutes === 0)
          ) {
            return {
              ...currentResult,
              [currentKey]: stats?.travelTime?.previsional?.[currentKey],
            };
          }

          return currentResult;
        },
        {}
      );
    }

    return {
      toggle: true,
      labels: Object.keys(realTimeData)?.map((v) => {
        switch (axisType) {
          case "HOUR":
            return `${v}h`;
          case "5MIN":
            return v;
          default:
            return v;
        }
      }),
      datasets: [
        {
          type: "line",
          label: intl.messages["flowScene.charts.attendance.realTime"],
          data: Object.values(realTimeData),
          fill: false,
          borderColor: ParkkiTheme.colors.red,
          backgroundColor: ParkkiTheme.colors.red,
          pointBorderColor: ParkkiTheme.colors.red,
          pointBackgroundColor: ParkkiTheme.colors.red,
          pointHoverBackgroundColor: ParkkiTheme.colors.red,
          pointHoverBorderColor: ParkkiTheme.colors.red,
        },
        {
          type: "bar",
          label: intl.messages["flowScene.charts.attendance.previsional"],
          data: Object.values(previsionalData),
          backgroundColor: FlowUtils.getHourBackgroundColorArray(
            get(
              Object.values(realTimeData).filter((item) => item !== null),
              "length",
              0
            ) - 1,
            0,
            axisType
          ),
          borderColor: ParkkiTheme.colors.turquoise,
          hoverBackgroundColor: ParkkiTheme.colors.turquoise,
          hoverBorderColor: ParkkiTheme.colors.turquoise,
        },
      ],
    };
  };

  static getTravelTimeChartOptions = (stats, intl) => {
    const durationUnit = stats.travelTime.duration_unit;
    return {
      maintainAspectRatio: false,
      responsive: true,
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 12,
        },
      },
      tooltips: {
        enabled: true,
        mode: "index",
        callbacks: {
          title: (tooltipItems, data) => {
            return `${
              intl.messages["flowScene.charts.attendance.tooltipTitlePrefixAt"]
            } ${get(data, "labels", {})[tooltipItems[0].index]}`;
          },
          label: (
            tooltipItem = { datasetIndex: null, index: null },
            data = { datasets: [] }
          ) => {
            const { datasetIndex, index } = tooltipItem;
            const { datasets } = data;
            const currentDataset =
              datasetIndex >= 0 &&
              datasets[datasetIndex] !== null &&
              datasets[datasetIndex] !== undefined
                ? datasets[datasetIndex]
                : null;

            return (
              CommonUtils.humanizeDateFromDuration(
                currentDataset?.data?.[index],
                stats.travelTime.duration_unit.toLowerCase(),
                intl,
                false
              ) || intl.messages["flowScene.charts.attendance.noData"]
            );
          },
        },
      },
      scales: {
        xAxes: [
          {
            categoryPercentage: 1.0,
            barPercentage: 0.95,
            ticks: {
              autoSkip: true,
              maxTicksLimit: 10,
            },
            gridLines: {
              display: false,
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              userCallback: (value) => {
                if (durationUnit === "SECONDS") {
                  return CommonUtils.humanizeDateFromDuration(
                    value,
                    durationUnit.toLowerCase(),
                    intl,
                    false
                  );
                }
                const hours = Math.floor(value / 60);
                const minutes = Math.floor(value % 60);
                const seconds = Math.round((value - Math.floor(value)) * 60);

                return (
                  (hours > 0 ? `${hours}h` : "") +
                  (minutes > 0 ? `${minutes}m` : "") +
                  (seconds > 0 ? `${seconds}s` : "")
                );
              },
            },
          },
        ],
      },
    };
  };

  static getAttendanceChartOptions = (stats, intl) => {
    const axisType = FlowUtils.getAttendanceChartAxis(
      get(stats, "filters", {})
    );

    return {
      maintainAspectRatio: false,
      responsive: true,
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 12,
        },
      },
      tooltips: {
        enabled: true,
        mode: "index",
        callbacks: {
          title: (tooltipItems, data) =>
            `${
              intl.messages[
                `flowScene.charts.attendance.${
                  axisType !== "DAY"
                    ? "tooltipTitlePrefixAt"
                    : "tooltipTitlePrefixThe"
                }`
              ]
            } ${get(data, "labels", {})[tooltipItems[0].index]}`,
          label: (
            tooltipItem = { datasetIndex: null, index: null },
            data = { datasets: [] }
          ) => {
            const { datasetIndex, index } = tooltipItem;
            const { datasets } = data;
            const currentDataset = datasetIndex >= 0 && datasets[datasetIndex];
            const nbCars =
              (currentDataset &&
                currentDataset.data &&
                currentDataset.data[index]) ||
              intl.messages["flowScene.charts.attendance.any"];

            return `${datasets[datasetIndex].label}: ${CommonUtils.formatNumber(
              nbCars
            )} ${
              intl.messages[
                `flowScene.charts.attendance.count${nbCars > 1 ? "s" : ""}`
              ]
            }`;
          },
        },
      },
      scales: {
        xAxes: [
          {
            categoryPercentage: 1.0,
            barPercentage: 0.95,
            ticks: {
              autoSkip: true,
              maxTicksLimit: 10,
            },
            gridLines: {
              display: false,
            },
          },
        ],
        yAxes: [],
      },
    };
  };

  static getLineCharts = (
    stats = [],
    type,
    intl,
    timeLimit,
    trafficDetails,
    chronologyAxis
  ) => {
    if (type === "area") {
      return FlowUtils.getLineChartsForArea(
        stats,
        intl,
        timeLimit,
        chronologyAxis
      );
    }
    if (type === "point") {
      return FlowUtils.getLineChartsForPoint(
        stats,
        intl,
        timeLimit,
        trafficDetails,
        chronologyAxis
      );
    }

    return [];
  };

  static getLineChartsForPoint = (
    stats = [],
    intl,
    timeLimit,
    trafficDetails,
    chronologyAxis
  ) => {
    let charts = [];

    stats.forEach((stat) => {
      charts = [
        ...charts,
        FlowUtils.getPointLineChart(
          stat,
          intl,
          timeLimit,
          trafficDetails,
          chronologyAxis
        ),
      ];
    });

    // We add a final "total" chart
    charts = [...charts, this.getTotalLineChart(charts, intl)];

    return charts;
  };

  static getLineChartsForArea = (
    stats = [],
    intl,
    timeLimit,
    chronologyAxis
  ) => {
    let charts = [];

    // Sort each stats by point ID and IN/OUT
    const categorisedStats = stats.reduce((acc, stat) => {
      return {
        ...acc,
        [stat.point_id]: {
          ...get(acc, get(stat, "point_id"), {}),
          [stat.orientation]: stat,
        },
      };
    }, {});

    // for each Point and each IN/OUT we create a graph
    Object.keys(categorisedStats).forEach((id, index) => {
      Object.keys(get(categorisedStats, id, {})).forEach((orientation) => {
        charts = [
          ...charts,
          FlowUtils.getAreaLineChartForPointAndOrientation(
            stats,
            id,
            orientation,
            timeLimit,
            chronologyAxis
          ),
        ];
      });
    });

    // We add a final "total" chart
    charts = [...charts, this.getTotalLineChart(charts, intl, chronologyAxis)];

    return charts;
  };

  static getTotalLineChart = (charts, intl, chronologyAxis) => {
    let data = Array(24).fill(0);
    if (chronologyAxis === "24H") {
      data = charts.reduce((acc, chart) => {
        return acc.map((v, index) => v + get(chart, `data[${index}]`, 0));
      }, data);
    } else {
      data = charts.reduce((acc, chart) => {
        return chart.data.map((v, index) => v + get(acc, index, 0));
      }, data);
    }

    return {
      type: "line",
      label: intl.messages["flowScene.charts.chronology.total"],
      data,
      fill: true,
      fillOpacity: 0.4,
      borderWidth: 2,
      borderColor: ParkkiTheme.colors.red,
      backgroundColor: "rgba(255, 10, 13, 0.4)",
      pointBorderColor: ParkkiTheme.colors.red,
      pointBackgroundColor: ParkkiTheme.colors.red,
      pointHoverBackgroundColor: ParkkiTheme.colors.red,
      pointHoverBorderColor: ParkkiTheme.colors.red,
    };
  };

  static getChronologyLimitTime = (stats) => {
    const chronologyConfig = get(stats, "chronology", {});
    return (
      chronologyConfig?.timeLimit ?? {
        left_time_limit: 0,
        right_time_limit: 24,
      }
    );
  };

  static getAreaLineChartForPointAndOrientation = (
    stats = [],
    id,
    orientation,
    timeLimit,
    chronologyAxis
  ) => {
    let data = Array(
      Math.abs(timeLimit.right_time_limit - timeLimit.left_time_limit) +
        (timeLimit.right_time_limit === 24 ? 0 : 1)
    ).fill(0);
    if (chronologyAxis !== "24H") {
      data = stats
        .filter(
          (stat) =>
            get(stat, "point_id") === id &&
            get(stat, "orientation") === orientation
        )
        .reduce((acc, stat) => {
          const d = Object.values(stat.data);
          return d.map((v, index) => (acc[index] = get(acc, index, 0) + v));
        }, data);
    }
    let color;
    let name;

    stats
      .filter(
        (stat) =>
          get(stat, "point_id") === id &&
          get(stat, "orientation") === orientation
      )
      .forEach((stat) => {
        color = get(stat, "display.selection_color");
        name = get(stat, "long_name");
        return Object.values(
          CommonUtils.formatGraphWithTimeLimit(get(stat, "data", {}), timeLimit)
        ).forEach(
          (value, index) => (data[index] = get(data, index, 0) + value)
        );
      });

    data = orientation.toLowerCase() === "out" ? data.map((r) => r * -1) : data;

    return {
      type: "line",
      label: FlowUtils.getAreaLineChartLabel(orientation, name),
      data,
      fill: false,
      borderWidth: 1,
      borderDash: orientation.toLowerCase() === "out" ? [5, 5] : [],
      borderColor: color,
      backgroundColor: "rgba(255, 255, 255, 0)",
      pointBorderColor: color,
      pointBackgroundColor: color,
      pointHoverBackgroundColor: color,
      pointHoverBorderColor: color,
    };
  };

  static getPointLineChartLabel = (orientation, type, intl, traffic) => {
    let label = "";

    if (orientation.toLowerCase() === "in") {
      label = `${label}[${traffic?.labels?.in ?? "E"}] - `;
    } else if (orientation.toLowerCase() === "out") {
      label = `${label}[${traffic?.labels?.out ?? "S"}] - `;
    }

    label = `${label}${
      intl.messages[`flowScene.flowType.short_${type.toLowerCase()}`]
    }`;

    return label;
  };

  static getAreaLineChartLabel = (orientation, name) => {
    let label = "";

    if (orientation.toLowerCase() === "in") {
      label = `${label}[E] - `;
    } else if (orientation.toLowerCase() === "out") {
      label = `${label}[S] - `;
    }

    return `${label}${name}`;
  };

  static getPointLineChart = (
    stats = [],
    intl,
    timeLimit,
    trafficDetails,
    chronologyAxis
  ) => {
    let data = Array(
      Math.abs(timeLimit.right_time_limit - timeLimit.left_time_limit) +
        (timeLimit.right_time_limit === 24 ? 0 : 1)
    ).fill(0);
    if (chronologyAxis !== "24h") {
      data = Object.values(stats.data).map((v) => (v === null ? 0 : v));
    }
    const color = FlowUtils.getColorFromType(get(stats, "type"));
    Object.keys(
      CommonUtils.formatGraphWithTimeLimit(stats?.data ?? {}, timeLimit)
    ).forEach((key, index) => {
      data[index] = get(stats, `data[${key}]`, 0);
    });

    data =
      get(stats, "orientation", "").toLowerCase() === "out"
        ? data.map((r) => r * -1)
        : data;

    return {
      type: "line",
      hidden: true,
      label: FlowUtils.getPointLineChartLabel(
        get(stats, "orientation"),
        get(stats, "type"),
        intl,
        trafficDetails?.[stats?.point_id]
      ),
      borderWidth: 1,
      data,
      fill: false,
      borderDash:
        get(stats, "orientation", "").toLowerCase() === "out" ? [5, 5] : [],
      borderColor: color,
      backgroundColor: "rgba(255, 255, 255, 0)",
      pointBorderColor: color,
      pointBackgroundColor: color,
      pointHoverBackgroundColor: color,
      pointHoverBorderColor: color,
    };
  };

  static getChronologyChart = (stats, intl, chronologyAxis) => {
    const timeLimit = FlowUtils.getChronologyLimitTime(stats);
    let labels = [];
    if (chronologyAxis === "24H") {
      labels = new Array(
        Math.abs(timeLimit.right_time_limit - timeLimit.left_time_limit) +
          (timeLimit.right_time_limit === 24 ? 0 : 1)
      )
        .fill(null)
        .map((v, index) => `${timeLimit.left_time_limit + index}h`);
    } else {
      const chronoStats = get(stats, "chronology.stats", []);
      if (chronoStats.length > 0) {
        labels = Object.keys(chronoStats[0].data).map((v) => {
          switch (chronologyAxis) {
            case "DAY":
              return moment(v).format("DD/MM/YYYY");
            case "HOUR":
              return v.includes(" ")
                ? moment(v).format("DD/MM HH:00")
                : `${v}h`;
            default:
              return v;
          }
        });
      } else {
        labels = [];
      }
    }

    return {
      // toggle: Abilities.ability.can("read", "park_stats.dailyAttendance"),
      toggle: true,
      labels,
      datasets: FlowUtils.getLineCharts(
        get(stats, "chronology.stats", []),
        get(stats, "chronology.type"),
        intl,
        timeLimit,
        stats?.traffic,
        chronologyAxis
      ),
    };
  };

  static getChronologyChartOptions = (stats, intl) => {
    return {
      maintainAspectRatio: false,
      responsive: true,
      legend: {
        position: "top",
        labels: {
          boxWidth: 12,
        },
      },
      tooltips: {
        enabled: true,
        mode: "index",
        callbacks: {
          label: (
            tooltipItem = { datasetIndex: null, index: null },
            data = { datasets: [] }
          ) => {
            const { datasetIndex, index } = tooltipItem;
            const { datasets } = data;
            const currentDataset = datasetIndex >= 0 && datasets[datasetIndex];
            const value =
              currentDataset &&
              currentDataset.data &&
              currentDataset.data[index];

            return `${datasets[datasetIndex].label}: ${CommonUtils.formatNumber(
              value
            )}`;
          },
        },
      },
      scales: {
        xAxes: [
          {
            ticks: {
              autoSkip: true,
              maxTicksLimit: 10,
            },
            gridLines: {
              display: false,
            },
          },
        ],
        yAxes: [],
      },
    };
  };

  static getHourBackgroundColorArray = (
    realTimeLength,
    previsionalLength,
    axisType
  ) => {
    const defaultBarColor = ParkkiTheme.colors.grey;
    const otherColor = ParkkiTheme.colors.turquoise3;
    let maxData = 31;

    if (axisType === "HOUR") {
      if (realTimeLength > 24) {
        maxData = previsionalLength;
      } else {
        maxData = 24;
      }
    } else if (axisType === "5MIN") {
      maxData = 288;
    }

    return Array(maxData)
      .fill()
      .map((v, i) => (i <= realTimeLength ? defaultBarColor : otherColor));
  };

  static getDayBackgroundColorArray = (realTimeValues) => {
    const defaultBarColor = ParkkiTheme.colors.grey;
    const otherColor = ParkkiTheme.colors.turquoise3;

    return Array(realTimeValues.length || 31)
      .fill()
      .map((v, i) =>
        realTimeValues[i] || realTimeValues[i] === 0
          ? defaultBarColor
          : otherColor
      );
  };

  static getIconRotation = (type) => {
    switch (type) {
      case "E":
      case "DH":
        return "90deg";
      case "W":
        return "-90deg";
      case "S":
        return "180deg";
      case "NE":
      case "DD":
        return "45deg";
      case "NW":
      case "DDR":
        return "-45deg";
      case "SW":
        return "-135deg";
      case "SE":
        return "135deg";
      default:
        return "0deg";
    }
  };

  static getPointIcon = (type) => {
    let icon = ImportExport;

    if (type[0] !== "D") {
      icon = ArrowUpward;
    }

    return styled(icon)`
      transform: rotate(${FlowUtils.getIconRotation(type)});
    `;
  };
}

export default FlowUtils;
